import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  ArrowLeft, 
  Save,
  Ticket,
  AlertCircle
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { cn, getPriorityColor } from '@/lib/utils';
import api from '@/services/api';

interface TicketFormData {
  subject: string;
  description: string;
  categoryId: string;
  priority: string;
  assetId: string | null;
  assigneeId: string | null;
}

interface FormErrors {
  subject?: string;
  description?: string;
  categoryId?: string;
  priority?: string;
}

const PRIORITIES = [
  { value: 'LOW', label: 'Low', description: 'Minor issue, no immediate impact' },
  { value: 'MEDIUM', label: 'Medium', description: 'Moderate impact, can wait' },
  { value: 'HIGH', label: 'High', description: 'Significant impact, needs attention' },
  { value: 'URGENT', label: 'Urgent', description: 'Critical business impact' },
  { value: 'CRITICAL', label: 'Critical', description: 'System down, immediate action required' },
];

export default function TicketFormPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isEditMode = Boolean(id);

  const [formData, setFormData] = useState<TicketFormData>({
    subject: '',
    description: '',
    categoryId: '',
    priority: 'MEDIUM',
    assetId: null,
    assigneeId: null,
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Fetch existing ticket for edit mode
  const { data: existingTicket, isLoading: isLoadingTicket } = useQuery({
    queryKey: ['ticket', id],
    queryFn: async () => {
      const response = await api.get(`/tickets/${id}`);
      return response.data.data;
    },
    enabled: isEditMode,
  });

  // Fetch ticket categories
  const { data: categoriesData, isLoading: isLoadingCategories } = useQuery({
    queryKey: ['ticketCategories'],
    queryFn: async () => {
      const response = await api.get('/tickets/public/categories');
      return response.data.data || [];
    },
  });

  // Fetch assets for optional linking
  const { data: assetsData } = useQuery({
    queryKey: ['assetsSimple'],
    queryFn: async () => {
      const response = await api.get('/assets?limit=100&status=ASSIGNED');
      return response.data.data;
    },
  });

  // Fetch technicians for assignment
  const { data: usersData } = useQuery({
    queryKey: ['technicians'],
    queryFn: async () => {
      const response = await api.get('/users?role=TECHNICIAN&limit=100');
      return response.data.data;
    },
  });

  // Populate form with existing ticket data in edit mode
  useEffect(() => {
    if (existingTicket) {
      setFormData({
        subject: existingTicket.title || existingTicket.subject || '',
        description: existingTicket.description || '',
        categoryId: existingTicket.categoryId || '',
        priority: existingTicket.priority || 'MEDIUM',
        assetId: existingTicket.assetId || null,
        assigneeId: existingTicket.assigneeId || null,
      });
    }
  }, [existingTicket]);

  // Create mutation
  const createMutation = useMutation({
    mutationFn: async (data: TicketFormData) => {
      const response = await api.post('/tickets', data);
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['tickets'] });
      navigate(`/tickets/${data.data.id}`);
    },
    onError: (error: any) => {
      setSubmitError(error.response?.data?.message || 'Failed to create ticket');
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: async (data: Partial<TicketFormData>) => {
      const response = await api.patch(`/tickets/${id}`, {
        subject: data.subject,
        description: data.description,
        categoryId: data.categoryId,
        priority: data.priority,
        assetId: data.assetId,
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ticket', id] });
      queryClient.invalidateQueries({ queryKey: ['tickets'] });
      navigate(`/tickets/${id}`);
    },
    onError: (error: any) => {
      setSubmitError(error.response?.data?.message || 'Failed to update ticket');
    },
  });

  const categories = categoriesData || [];
  const assets = assetsData || [];
  const users = usersData || [];

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.subject.trim()) {
      newErrors.subject = 'Title is required';
    } else if (formData.subject.length > 200) {
      newErrors.subject = 'Title must be less than 200 characters';
    }

    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    }

    if (!formData.categoryId) {
      newErrors.categoryId = 'Please select a category';
    }

    if (!formData.priority) {
      newErrors.priority = 'Please select a priority';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);

    if (!validateForm()) {
      return;
    }

    if (isEditMode) {
      updateMutation.mutate(formData);
    } else {
      createMutation.mutate(formData);
    }
  };

  const handleChange = (field: keyof TicketFormData, value: string | null) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when field is changed
    if (errors[field as keyof FormErrors]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const isLoading = isEditMode && isLoadingTicket;
  const isSubmitting = createMutation.isPending || updateMutation.isPending;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-3xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" asChild>
          <Link to={isEditMode ? `/tickets/${id}` : '/tickets'}>
            <ArrowLeft className="w-5 h-5" />
          </Link>
        </Button>
        <div>
          <h1 className="text-3xl font-bold">
            {isEditMode ? 'Edit Ticket' : 'Create New Ticket'}
          </h1>
          <p className="text-muted-foreground">
            {isEditMode 
              ? 'Update the ticket information below' 
              : 'Fill in the details to create a new support ticket'}
          </p>
        </div>
      </div>

      {/* Error Alert */}
      {submitError && (
        <Card className="border-destructive bg-destructive/10">
          <CardContent className="flex items-center gap-2 p-4">
            <AlertCircle className="w-5 h-5 text-destructive" />
            <p className="text-destructive">{submitError}</p>
          </CardContent>
        </Card>
      )}

      {/* Form */}
      <form onSubmit={handleSubmit}>
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Ticket className="w-5 h-5" />
              Ticket Information
            </CardTitle>
            <CardDescription>
              Provide details about the issue or request
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Title */}
            <div className="space-y-2">
              <Label htmlFor="subject">
                Title <span className="text-destructive">*</span>
              </Label>
              <Input
                id="subject"
                placeholder="Brief summary of the issue"
                value={formData.subject}
                onChange={(e) => handleChange('subject', e.target.value)}
                error={errors.subject}
                maxLength={200}
              />
              <p className="text-xs text-muted-foreground text-right">
                {formData.subject.length}/200
              </p>
            </div>

            {/* Description */}
            <div className="space-y-2">
              <Label htmlFor="description">
                Description <span className="text-destructive">*</span>
              </Label>
              <textarea
                id="description"
                className={cn(
                  "w-full min-h-[150px] p-3 rounded-md border bg-background text-sm resize-none focus:outline-none focus:ring-2 focus:ring-ring",
                  errors.description ? "border-destructive focus:ring-destructive" : "border-input"
                )}
                placeholder="Provide a detailed description of the issue, including any error messages, steps to reproduce, and what you were trying to accomplish..."
                value={formData.description}
                onChange={(e) => handleChange('description', e.target.value)}
              />
              {errors.description && (
                <p className="text-sm text-destructive">{errors.description}</p>
              )}
            </div>

            {/* Category */}
            <div className="space-y-2">
              <Label htmlFor="categoryId">
                Category <span className="text-destructive">*</span>
              </Label>
              <select
                id="categoryId"
                className={cn(
                  "w-full h-10 px-3 rounded-md border bg-background text-sm",
                  errors.categoryId ? "border-destructive" : "border-input"
                )}
                value={formData.categoryId}
                onChange={(e) => handleChange('categoryId', e.target.value)}
                disabled={isLoadingCategories}
              >
                <option value="">Select a category</option>
                {categories.map((category: any) => (
                  <option key={category.id} value={category.id}>
                    {category.icon} {category.name}
                  </option>
                ))}
              </select>
              {errors.categoryId && (
                <p className="text-sm text-destructive">{errors.categoryId}</p>
              )}
            </div>

            {/* Priority */}
            <div className="space-y-2">
              <Label>
                Priority <span className="text-destructive">*</span>
              </Label>
              <div className="grid gap-2 sm:grid-cols-5">
                {PRIORITIES.map((priority) => (
                  <button
                    key={priority.value}
                    type="button"
                    className={cn(
                      "p-3 rounded-lg border-2 transition-all text-left",
                      formData.priority === priority.value
                        ? "border-primary bg-primary/5"
                        : "border-input hover:border-muted-foreground/50"
                    )}
                    onClick={() => handleChange('priority', priority.value)}
                  >
                    <span className={cn(
                      "inline-block px-2 py-0.5 text-xs font-medium rounded-full mb-1",
                      getPriorityColor(priority.value)
                    )}>
                      {priority.label}
                    </span>
                    <p className="text-xs text-muted-foreground hidden sm:block">
                      {priority.description}
                    </p>
                  </button>
                ))}
              </div>
              {errors.priority && (
                <p className="text-sm text-destructive">{errors.priority}</p>
              )}
            </div>

            {/* Related Asset (Optional) */}
            <div className="space-y-2">
              <Label htmlFor="assetId">Related Asset (Optional)</Label>
              <select
                id="assetId"
                className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                value={formData.assetId || ''}
                onChange={(e) => handleChange('assetId', e.target.value || null)}
              >
                <option value="">No related asset</option>
                {assets.map((asset: any) => (
                  <option key={asset.id} value={asset.id}>
                    {asset.assetTag} - {asset.name}
                  </option>
                ))}
              </select>
              <p className="text-xs text-muted-foreground">
                Link this ticket to a specific asset if applicable
              </p>
            </div>

            {/* Assign To (Optional, only for new tickets) */}
            {!isEditMode && (
              <div className="space-y-2">
                <Label htmlFor="assigneeId">Assign To (Optional)</Label>
                <select
                  id="assigneeId"
                  className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                  value={formData.assigneeId || ''}
                  onChange={(e) => handleChange('assigneeId', e.target.value || null)}
                >
                  <option value="">Leave unassigned</option>
                  {users.map((user: any) => (
                    <option key={user.id} value={user.id}>
                      {user.firstName} {user.lastName} ({user.email})
                    </option>
                  ))}
                </select>
                <p className="text-xs text-muted-foreground">
                  Optionally assign this ticket to a technician
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Form Actions */}
        <div className="flex items-center justify-end gap-4 mt-6">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate(isEditMode ? `/tickets/${id}` : '/tickets')}
          >
            Cancel
          </Button>
          <Button 
            type="submit" 
            disabled={isSubmitting}
            loading={isSubmitting}
          >
            <Save className="w-4 h-4 mr-2" />
            {isEditMode ? 'Update Ticket' : 'Create Ticket'}
          </Button>
        </div>
      </form>
    </div>
  );
}
